import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { readBlobWithProgress } from '@/helpers/downloadProgress'
import {
  NOT_A_WORD_DOCUMENT_MESSAGE,
  isWordDocument,
  storedVersionOf,
} from '@/helpers/file'

import {
  type DocxVersionSource,
  type StoredDocx,
  fileQueryKeys,
} from '@/hooks/files/types'
import { fetchFile } from '@/hooks/files/useFile'

import { useFileDownloadStore } from '@/stores/FileDownloadStore'

import type { File } from '@/types/File'

/**
 * The record the download of a Word file is reading, with the URL it reads
 * by, for as long as that download is under way; null otherwise. Published by
 * the download rather than looked up beside it, so anything read ahead of the
 * download — a large file's opening — is the revision the download brings.
 */
export const useDownloadingDocxRecord = ({ fileId }: { fileId: string }) =>
  useQuery<File | null>({
    queryKey: fileQueryKeys.docxRecord({ fileId }),
    queryFn: skipToken,
    staleTime: Infinity,
  })

interface UseFileDocxBlobArgs {
  fileId: string
  /** Read one version of the file rather than the file as it stands. */
  version?: DocxVersionSource | null
  enabled?: boolean
}

/**
 * The bytes of a stored Word file, for a surface that reads the file itself
 * rather than a rendition of it.
 *
 * The file as it was last saved: turning what an open editor holds into a
 * version is a wait measured in tens of seconds. A surface that cares whether
 * it has fallen behind offers the reader the latest — see usePreviewFreshness.
 *
 * Held forever once fetched and shared with the editor's import path: the
 * presigned URL is minted fresh for every fetch, so nothing below this cache
 * can dedupe these downloads.
 */
export const useFileDocxBlob = ({
  fileId,
  version = null,
  enabled = true,
}: UseFileDocxBlobArgs) => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  // A version downloads alongside the file rather than instead of it.
  const progressKey = version?.id ?? fileId
  const recordKey = fileQueryKeys.docxRecord({ fileId })

  return useQuery({
    queryKey: version
      ? fileQueryKeys.versionDocx({ fileId, versionId: version.id })
      : fileQueryKeys.docx({ fileId }),
    queryFn: async () => {
      // Reported aside, for whoever is showing the wait; see FileDownloadStore.
      const { report, clear } = useFileDownloadStore.getState()
      report(progressKey, { loaded: 0, total: null })
      try {
        let url = version?.url
        let versionId = version?.id ?? null
        if (url === undefined) {
          queryClient.setQueryData(recordKey, null)
          // A fresh presigned URL: the one on the file record was minted with its list and may have expired.
          const file = await fetchFile(fileId, user?.accessToken, true)
          queryClient.setQueryData(recordKey, file)
          url = file.url
          versionId = file.current_version_id ?? null
        }
        const res = await fetch(url)
        if (!res.ok) {
          throw new Error(`Failed to download document (${res.status})`)
        }
        const blob = await readBlobWithProgress(res, (progress) =>
          report(progressKey, progress),
        )
        // The name and content type are only what the uploader claimed; bytes that
        // aren't a Word document crash the converter rather than failing politely.
        if (!(await isWordDocument(blob))) {
          throw new Error(NOT_A_WORD_DOCUMENT_MESSAGE)
        }
        // Off the download itself, so the version names the bytes in hand: reading the
        // file record a moment earlier leaves room for somebody else's save to land.
        // Bytes from an older version name none — a save must never take them for the file.
        return {
          blob,
          version: version ? null : storedVersionOf(res),
          versionId,
        } satisfies StoredDocx
      } catch (error) {
        clear(progressKey)
        throw error
      } finally {
        if (!version) queryClient.setQueryData(recordKey, null)
      }
    },
    enabled: enabled && !!fileId,
    staleTime: Infinity,
    // One retry, then the caller falls back: a reader waiting on a document is
    // better served by the PDF rendition than by another 30 seconds of backoff.
    retry: 1,
  })
}
